<template>
    <x-container fluid>
        <x-layout class="summary-header-row">
            <x-flex xs3>
                <h3><span class="secondary--text">Review</span> your submission</h3>
            </x-flex>

            <x-flex xs9 text-xs-right>
                <x-btn
                    v-if="submission.id > 0"
                    class="mr-2"
                    color="tertiary"
                    small
                    @click="$emit('submission:navigate-to', currentStep - 1)"
                >
                    <i class="material-icons mr-2">arrow_back</i>

                    Back
                </x-btn>

                <delete-quote-button v-if="submission.status === 'draft'" :submissionId="submission.id" small />

                <x-btn
                    class="ml-2"
                    color="tertiary"
                    :loading="previewPDFLoading"
                    small
                    @click="previewDocument"
                >
                    <i class="material-icons mr-2">browser_updated</i>

                    {{ previewDocButtonTitle }}
                </x-btn>

                <x-btn
                    v-if="isStandalonePolicy"
                    class="ml-4"
                    color="primary"
                    small
                    :loading="documentsLoading"
                    @click="submitStandalonePolicy"
                >
                    Submit

                    <i class="material-icons ml-2">trending_flat</i>
                </x-btn>

                <x-btn
                    v-else
                    class="ml-4"
                    color="primary"
                    small
                    :loading="documentsLoading"
                    @click="goToSelectInsurers()"
                >
                    Select {{ insurerType }}s

                    <i class="material-icons ml-2">trending_flat</i>
                </x-btn>
            </x-flex>
        </x-layout>

        <x-layout>
            <x-flex xs12>
                <summary-tabs-buttons
                    v-model="activeTab"
                    :tabs="summaryTabs"
                />
            </x-flex>
        </x-layout>

        <x-layout v-show="activeTab === 'product'">
            <x-flex xs6 class="left">
                <x-card class="panel">
                    <x-card-title>
                        <h3>Submission information</h3>
                    </x-card-title>

                    <x-card-text>
                        <standalone-policy-summary-data-list
                            v-if="isStandalonePolicy"
                            v-model="submission"
                            :multipleEnitities="standalonePolicyWithMultipleEntities"
                        />

                        <summary-data-list data-test="submission_information" v-else v-model="submission" :facilities="facilities" />
                    </x-card-text>
                </x-card>
            </x-flex>

            <x-flex xs6 class="right">
                <x-card v-if="standalonePolicyWithMultipleEntities" class="panel mb-5">
                    <x-card-title>
                        <h3>Entities</h3>
                    </x-card-title>

                    <x-card-text>
                        <submission-entity-data-list v-model="submission.entities" />
                    </x-card-text>
                </x-card>

                <x-card v-if="hasObligor || (isStandalonePolicy && submission.obligors.length > 0)" class="panel mb-5">
                    <x-card-title>
                        <h3>Obligors</h3>
                    </x-card-title>

                    <x-card-text>
                        <obligor-data-list v-model="submission.obligors" obligor :currency-code="submission.currency.code" />
                    </x-card-text>
                </x-card>

                <x-card v-if="hasObligor || (isStandalonePolicy && submission.guarantors.length > 0)" class="panel">
                    <x-card-title>
                        <h3>Guarantors</h3>
                    </x-card-title>

                    <x-card-text>
                        <obligor-data-list v-model="submission.guarantors" />
                    </x-card-text>
                </x-card>
            </x-flex>
        </x-layout>

        <x-layout v-show="activeTab === 'additional_information'">
            <x-layout>
                <x-flex>
                    <additional-information-tab
                        v-model="submission.information_sections"
                        :facility-id="submission.facility_id"
                    />
                </x-flex>
            </x-layout>
        </x-layout>

        <x-layout v-show="activeTab === 'documents'">
            <x-flex xs12>
                <document-list
                    upload
                    :documentable-id="submission.id"
                    :facility-id="submission.facility_id"
                    insurer-specific-upload
                    documentable-type="submission" :is-standalone-policy="submission.risk_structure === 'standalone_policy'"
                    :allowPolicyWordingAndMRCHandling="isStandalonePolicy"
                    :insurerList="selectedInsurers"
                    :allowSetPermissions="isConvertingFromManualSubmission"
                    :mstDocuments="documents"
                    :isPreFirmOrder="isPreFirmOrder"
                    @update-documents="updateDocuments"
                />
            </x-flex>
        </x-layout>

        <modal ref="errorModal" title="Error">
            <p v-if="!hasPolicyWordingDocument">Please upload the Policy Wording</p>
        </modal>

        <modal ref="warningModal" title="Warning" confirmation>
            <p>You have not set permissions for all documents, you will not be able to repeat this action, do you want to continue?</p>
        </modal>
    </x-container>
</template>

<script>
    import DocumentList from '~/components/Shared/UIComponents/DocumentList.vue';
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import submissionHelpers from "../../../mixins/submissionHelpers";
    import Modal from "../../Shared/UIComponents/Modal.vue";
    import DeleteQuoteButton from '~/components/Broker/UIComponents/DeleteQuoteButton';
    import StandalonePolicySummaryDataList from "~/components/Shared/UIComponents/StandalonePolicySummaryDataList.vue";
    import SubmissionInsuredDataList from "~/components/Shared/UIComponents/SubmissionInsuredDataList.vue";
    import SubmissionEntityDataList from "~/components/Shared/UIComponents/SubmissionEntityDataList.vue";

    export default {
        name: 'BrokerSummary',
        props: {
            submission: {},
            currentStep: Number,
            facilities: {
                type: Array,
                default: () => [],
            },
            standalonePolicyFirmOrderData: {},
            selectedInsurers: [],
            isConvertingFromManualSubmission: {
                type: Boolean,
                required: true
            },
        },
        components: {
            Modal,
            DeleteQuoteButton,
            DocumentList,
            SummaryTabsButtons,
            SummaryDataList,
            AdditionalInformationTab,
            ObligorDataList,
            StandalonePolicySummaryDataList,
            SubmissionInsuredDataList,
            SubmissionEntityDataList,
        },
        mixins: [submissionHelpers],
        data() {
            return {
                activeTab: 'product',
                previewPDFLoading: false,
                documents: [],
                documentsLoading: false,
                showSubmitError: false,
                submitingStandalonePolicy: false,
                warningDisplayed: false,
            }
        },
        mounted() {
            this.$eventBus.$on('documentsChanged', this.getSubmissionDocuments);
            this.getSubmissionDocuments();
        },
        beforeDestroy() {
            this.$eventBus.$off('documentsChanged', this.getSubmissionDocuments);
        },
        computed: {
            summaryTabs() {
                const tabs = [
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.showAdditionalInformation) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                tabs.push(
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            hasPolicyWordingDocument() {
                return this.documents.some(doc => doc.document_category_key === 'policy_wording');
            },
            insurerType() {
                if (this.submission.hasOwnProperty('captive_insurer_company_id') && this.submission.captive_insurer_company_id) {
                    return 'reinsurer';
                }

                return 'insurer'
            },
            hasObligor() {
                return this.submission.hasOwnProperty('product') && !this.submission.product.has_no_obligor;
            },
            showAdditionalInformation() {
                return !this.isStandalonePolicy && this.submission.information_sections.length > 0;
            },
            standalonePolicyWithMultipleEntities() {
                return this.isStandalonePolicy && this.submission.entities.length > 1;
            },
            submitButtonLoading() {
                return this.documentsLoading || this.submitingStandalonePolicy;
            },
            previewDocButtonTitle() {
                return this.isStandalonePolicy ? 'Download draft MRC' : 'Download draft Dec';
            },
            isPreFirmOrder() {
                return ['in_the_market', 'draft'].includes(this.submission.status);
            },
        },
        methods: {
            toggleActiveTab(newTab) {
                this.activeTab = newTab.value;
            },
            async goToSelectInsurers() {
                if (this.isConvertingFromManualSubmission && !this.warningDisplayed) {
                    const documentPermissionsNotSet = this.documents.some(({permissions}) => {
                        if (permissions === undefined || permissions === null) {
                            return true;
                        }

                        // Permission is already added for broker who upload document.
                        // So, warning should be shown when only one permission is set (broker only)
                        return permissions.length < 2;
                    });

                    if (documentPermissionsNotSet) {
                        if (await this.$refs.warningModal.open() === false) {
                            this.warningDisplayed = true;
                            return;
                        }
                    }
                }

                this.$emit('submission:navigate-to', this.currentStep + 1);
            },
            getSubmissionDocuments() {
                this.documentsLoading = true;
                axios.get(`/api/documents/submission/${this.submission.id}/with-permissions`).then(res => {
                    this.documents = res.data.data || [];
                    this.documentsLoading = false;
                });
            },
            submitStandalonePolicy() {
                if (!this.hasPolicyWordingDocument) {
                    this.$refs.errorModal.open();
                    return false;
                }

                this.submitingStandalonePolicy = true;

                axios.post(`/api/broker/submission/${this.submission.id}/manual-firm-order`, this.standalonePolicyFirmOrderData)
                .then(() => {
                    this.submitingStandalonePolicy = false;
                    this.$router.push('/agent/dashboard');
                });
            },
            async previewDocument() {
                this.previewPDFLoading = true;
                let fileUrl;
                if (this.isStandalonePolicy) {
                    fileUrl = (await axios.get(this.$root.api(`broker/submission/${this.submission.id}/standalone-policy/draft-mrc-download`))).data.url;
                } else {
                    fileUrl = (await axios.get(this.$root.api(`broker/submission/${this.submission.id}/download`))).data.url;
                }
                this.previewPDFLoading = false;
                window.location.href = fileUrl;

                this.status = 'idle';
            },
            updateDocuments(documents) {
                this.documents = documents;
            },
        }
    }
</script>

<style lang="scss" scoped>
    div.container.step-form {
        width: auto;
        margin: 66px -42px 0;
        padding: 0;
    }

    .summary-header-row {
        margin-bottom: 32px;
    }
</style>

